import useMinimumLoading from "../../hooks/useMinimumLoading";
import GroupSkeleton from "../../components/loading/GroupSkeleton";
import React, {useEffect, useState} from "react";
import {motion} from "motion/react";

import BGirlMimoji from "../../assets/images/BGirlMimoji.png";
import GirlMimoji from "../../assets/images/GirlMimoji.png";
import SunglassMimoji from "../../assets/images/SunglassMimoji.png";
import {LeftFriendsLine} from "../../assets/svgComponents/inviteFriendsPage/LeftFriendsLine";
import {RightFriendsLine} from "../../assets/svgComponents/inviteFriendsPage/RightFriendsLine";
import {PlaneIcon} from "../../assets/svgComponents/PlaneIcon";
import {SmallRemoveIcon} from "../../assets/svgComponents/SmallRemoveIcon";
import {DecorationUnderline} from "../../assets/svgComponents/inviteFriendsPage/DecorationUnderline";
import {EmailTickIcon} from "../../assets/svgComponents/EmailTickIcon";
import {VerticalSeparatorLine} from "../../assets/svgComponents/VerticalSeparatorLine";
import {EmailIcon} from "../../assets/svgComponents/EmailIcon";
import {HyperlinkIcon} from "../../assets/svgComponents/HyperlinkIcon";
import {GroupSessionService} from "../../services/GroupSessionService";
import {useNavigate, useParams} from "react-router-dom";
import {FarewiseIconLight} from "../../assets/svgComponents/FarewiseIconLight";


type Props = {
    onBack?: () => void;
    onNavigateToPreferences?: () => void;
    sessionLink?: string;
};

export default function SessionFriendsInvitePage({
                                                     onBack,
                                                     onNavigateToPreferences,
                                                     sessionLink,
                                                 }: Props) {
    const navigate = useNavigate();
    const {sessionId} = useParams<{ sessionId: string }>();

    const [linkCopied, setLinkCopied] = useState(false);
    const [email, setEmail] = useState("");
    const [invitedEmails, setInvitedEmails] = useState<string[]>([]);

    const [invitesLoading, setInvitesLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    const canAddEmail = isValidEmail(email.trim()) && !invitedEmails.includes(email.trim().toLowerCase());

    const getCopyUrl = () => `${window.location.origin}/sessions/${sessionId}/join`;

    const handleCopyLink = async () => {
        const url = getCopyUrl();
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
            } else {
                const ta = document.createElement("textarea");
                ta.value = url;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        } catch {
            setError("Could not copy the invitation link.");
        }
    };

    // ----------- Bestehende INVITED-Members beim Laden ziehen -----------
    useEffect(() => {
        let abort = false;

        async function loadInvites() {
            if (!sessionId) return;
            try {
                const statusItems = await GroupSessionService.getSessionStatus(sessionId);
                const invites = (statusItems || [])
                    .filter((m: any) => m?.status === "INVITED")
                    .map((m: any) => m?.email)
                    .filter(Boolean);
                if (!abort) setInvitedEmails(invites);
            } catch (e) {
                if (!abort) setError("Could not load invitations.");
            } finally {
                if (!abort) setInvitesLoading(false);
            }
        }

        loadInvites();
        return () => {
            abort = true;
        };
    }, [sessionId]);

    // ----------- Einladen -----------
    const handleAddEmail = async () => {
        if (!sessionId || !canAddEmail || loading) return;
        setError(null);
        setLoading(true);
        try {
            await GroupSessionService.invite(sessionId, email.trim().toLowerCase());
            setInvitedEmails((prev) => [...prev, email.trim().toLowerCase()]);
            setEmail("");
        } catch (e) {
            setError("Could not invite this email address. Please check it and try again.");
        } finally {
            setLoading(false);
        }
    };

    // ----------- Invite entfernen -----------
    const handleRemoveEmail = async (emailToRemove: string) => {
        if (!sessionId || loading) return;
        setError(null);
        setLoading(true);
        try {
            await GroupSessionService.removeInvite(sessionId, emailToRemove);
            setInvitedEmails((prev) => prev.filter((em) => em !== emailToRemove));
        } catch (e) {
            setError("Could not remove the invitation. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const SubmitFriendsRequests = async () => {
        try {
            setError(null);
        setLoading(true);

            navigate(`/sessions/${sessionId}/preferences`);
        } catch (err) {
            console.error("Error creating session:", err);
        } finally {
            setLoading(false);
        }
    };

    const showInvitesLoading = useMinimumLoading(invitesLoading);

    return (
        <motion.div
            className="bg-gradient-to-b from-[#f6f8ff] from-[43.912%] to-[#dde5ff] relative h-screen w-full"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.25, ease: "easeOut"}}
        >
            {error && <p role="alert" className="absolute top-6 inset-x-6 text-center text-red-600">{error}</p>}
            {/* Floating Clouds */}
            <motion.div
                className="absolute left-[85px] top-[20px] h-[93.333px] w-[125.688px] -z-10"
                initial={{opacity: 0, y: -20}}
                animate={{opacity: 1, y: [0, -12, 0]}}
                transition={{
                    opacity: {duration: 0.3, delay: 1.3, ease: "easeOut"},
                    y: {duration: 9, repeat: Infinity, ease: "easeInOut", delay: 1.5},
                }}
            >
                {/*<img className="block max-w-none size-full" src={CloudRight} alt="cloud-left" />*/}
            </motion.div>

            {/* Top-right: FarewiseIcon -> back */}
            <button
                type="button"
                aria-label="Back"
                className="absolute left-28 top-20 z-50 hover:scale-105"
                onClick={() => navigate(`/sessions/`)}
            >
                <FarewiseIconLight/>
            </button>

            <motion.div
                className="absolute left-1/2 top-[220px] h-[93.333px] w-[125.688px] z-30 translate-x-[370px]"
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: [0, 10, 0]}}
                transition={{
                    opacity: {duration: 0.3, delay: 1.4, ease: "easeOut"},
                    y: {duration: 7.5, repeat: Infinity, ease: "easeInOut", delay: 2},
                }}
            >
                {/*<img className="block max-w-none size-full" src={CloudRight} alt="cloud-right" />*/}
            </motion.div>

            <motion.div
                className="absolute left-1/2 top-[370px] h-[93.333px] w-[125.688px] z-10 translate-x-[-590px] scale-x-[-1]"
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: [0, 10, 0]}}
                transition={{
                    opacity: {duration: 0.3, delay: 1.6, ease: "easeOut"},
                    y: {duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2.5},
                }}
            >
                {/*<img className="block max-w-none size-full" src={BGirlMimoji} alt="cloud-left-mirror" />*/}
            </motion.div>

            {/* Content */}
            <div className="flex h-full w-full items-center justify-center relative">
                <div
                    className="box-border flex h-full w-full flex-col items-center justify-center gap-2.5 px-[72px] relative">
                    <div className="content-stretch flex items-center justify-center relative w-full">
                        <div
                            className="content-stretch flex flex-col items-start justify-start gap-[47px] relative w-[957.5px]">
                            {/* Headline + Unterstrich + Subtext */}
                            <div
                                className="content-stretch flex items-center justify-start gap-[35px] leading-[0] relative w-full">
                                <div
                                    className="content-stretch flex flex-col items-start justify-start gap-[34px] h-[143.333px] relative w-[490.833px]">
                                    <div
                                        className="inline-grid grid-cols-[max-content] grid-rows-[max-content] place-items-start relative">
                                        <motion.div
                                            className="[grid-area:1_/_1] h-[4.396px] ml-[262.099px] mt-[52.75px] relative w-[185.172px]"
                                            initial={{opacity: 0, scaleX: 0}}
                                            animate={{opacity: 1, scaleX: 1}}
                                            transition={{duration: 0.4, delay: 0.3, ease: "easeOut"}}
                                        >
                                            <div className="absolute inset-[-187.5%_-4.45%_-187.51%_-4.45%]">
                                                <DecorationUnderline/>
                                            </div>
                                        </motion.div>

                                        <motion.div
                                            className="[grid-area:1_/_1] font-outfit font-semibold leading-[0] ml-0 mt-0 not-italic relative text-[#232323] text-[54.947px] text-nowrap tracking-[-1.2088px]"
                                            initial={{opacity: 0, y: -30}}
                                            animate={{opacity: 1, y: 0}}
                                            transition={{duration: 0.25, delay: 0.1, ease: "easeOut"}}
                                        >
                                            <p className="leading-[normal] whitespace-pre">Invite your friends.</p>
                                        </motion.div>
                                    </div>

                                    <motion.p
                                        className="basis-0 grow min-h-px min-w-px font-outfit font-medium text-[#858ca7] text-[16.667px] tracking-[-0.3667px] w-full leading-normal"
                                        initial={{opacity: 0, y: -20}}
                                        animate={{opacity: 1, y: 0}}
                                        transition={{duration: 0.2, delay: 0.2, ease: "easeOut"}}
                                    >
                                        Invite your friends, share your preferences, and let AI find the perfect
                                        destination and time for your trip together.
                                    </motion.p>

                                </div>

                                {/* Avatar-Cluster + Emojis + Chat-Bubbles */}
                                <motion.div
                                    className="inline-grid grid-cols-[max-content] grid-rows-[max-content] place-items-start relative shrink-0 scale-90"
                                    initial={{opacity: 0, scale: 0.81}}
                                    animate={{opacity: 1, scale: 0.9}}
                                    transition={{duration: 0.3, delay: 0.4, ease: "easeOut"}}
                                >
                                    {/* Avatare */}
                                    <motion.div
                                        className="[grid-area:1_/_1] flex h-[101.545px] items-center justify-center ml-[260.001px] mt-[55.833px] relative w-[101.545px]"
                                        initial={{opacity: 0, scale: 0.8}}
                                        animate={{opacity: 1, scale: 1}}
                                        transition={{duration: 0.2, delay: 0.6, ease: "easeOut"}}
                                    >
                                        <div className="flex-none rotate-[353.427deg]">
                                            <div
                                                className="pointer-events-none relative size-[91.667px] rounded-[25px] bg-[rgba(255,255,255,0.5)]">
                                                <div aria-hidden
                                                     className="absolute inset-0 rounded-[25px] border-[1.667px] border-[rgba(255,255,255,0.5)] border-solid shadow-[0px_0px_13.333px_0px_rgba(0,0,0,0.09)]"/>
                                                <div
                                                    className="absolute inset-0 shadow-[0px_0px_10px_0px_inset_rgba(255,255,255,0.8)]"/>
                                            </div>
                                        </div>
                                    </motion.div>

                                    <motion.div
                                        className="[grid-area:1_/_1] flex h-[74.783px] items-center justify-center ml-[274.135px] mt-[72.07px] relative w-[74.783px]"
                                        initial={{opacity: 0, scale: 0.8}}
                                        animate={{opacity: 1, scale: 1}}
                                        transition={{duration: 0.2, delay: 0.65, ease: "easeOut"}}
                                    >
                                        <div className="flex-none rotate-[353.427deg]">
                                            <div
                                                className="size-[67.5px] bg-center bg-cover bg-no-repeat"
                                                style={{backgroundImage: `url('${SunglassMimoji}')`}}
                                                aria-label="avatar-1"
                                            />
                                        </div>
                                    </motion.div>

                                    <motion.div
                                        className="[grid-area:1_/_1] flex h-[95.953px] items-center justify-center ml-[180px] mt-[19.167px] relative w-[95.953px]"
                                        initial={{opacity: 0, scale: 0.8}}
                                        animate={{opacity: 1, scale: 1}}
                                        transition={{duration: 0.2, delay: 0.5, ease: "easeOut"}}
                                    >
                                        <div className="flex-none rotate-[2.753deg]">
                                            <div
                                                className="pointer-events-none relative size-[91.667px] rounded-[25px] bg-[rgba(255,255,255,0.5)]">
                                                <div aria-hidden
                                                     className="absolute inset-0 rounded-[25px] border-[1.667px] border-[rgba(255,255,255,0.5)] border-solid shadow-[0px_0px_13.333px_0px_rgba(0,0,0,0.09)]"/>
                                                <div
                                                    className="absolute inset-0 shadow-[0px_0px_10px_0px_inset_rgba(255,255,255,0.8)]"/>
                                            </div>
                                        </div>
                                    </motion.div>

                                    <motion.div
                                        className="[grid-area:1_/_1] flex h-[76.766px] items-center justify-center ml-[189.477px] mt-[31.261px] relative w-[76.766px]"
                                        initial={{opacity: 0, scale: 0.8}}
                                        animate={{opacity: 1, scale: 1}}
                                        transition={{duration: 0.2, delay: 0.55, ease: "easeOut"}}
                                    >
                                        <div className="flex-none rotate-[2.753deg]">
                                            <div
                                                className="size-[73.333px] bg-center bg-cover bg-no-repeat"
                                                style={{backgroundImage: `url('${BGirlMimoji}')`}}
                                                aria-label="avatar-2"
                                            />
                                        </div>
                                    </motion.div>

                                    <motion.div
                                        className="[grid-area:1_/_1] flex h-[104.247px] items-center justify-center ml-[97.501px] mt-[52.871px] relative w-[104.247px]"
                                        initial={{opacity: 0, scale: 0.8}}
                                        animate={{opacity: 1, scale: 1}}
                                        transition={{duration: 0.2, delay: 0.45, ease: "easeOut"}}
                                    >
                                        <div className="flex-none rotate-[351.463deg]">
                                            <div
                                                className="pointer-events-none relative size-[91.667px] rounded-[25px] bg-[rgba(255,255,255,0.5)]">
                                                <div aria-hidden
                                                     className="absolute inset-0 rounded-[25px] border-[1.667px] border-[rgba(255,255,255,0.5)] border-solid shadow-[0px_0px_13.333px_0px_rgba(0,0,0,0.09)]"/>
                                                <div
                                                    className="absolute inset-0 shadow-[0px_0px_10px_0px_inset_rgba(255,255,255,0.8)]"/>
                                            </div>
                                        </div>
                                    </motion.div>

                                    <motion.div
                                        className="[grid-area:1_/_1] flex h-[73.621px] items-center justify-center ml-[112.819px] mt-[69.856px] relative w-[73.621px]"
                                        initial={{opacity: 0, scale: 0.8}}
                                        animate={{opacity: 1, scale: 1}}
                                        transition={{duration: 0.2, delay: 0.5, ease: "easeOut"}}
                                    >
                                        <div className="flex-none rotate-[351.784deg]">
                                            <div
                                                className="size-[65px] bg-center bg-cover bg-no-repeat"
                                                style={{backgroundImage: `url('${GirlMimoji}')`}}
                                                aria-label="avatar-3"
                                            />
                                        </div>
                                    </motion.div>

                                    {/* Emojis */}
                                    <div
                                        className="[grid-area:1_/_1] flex h-[24.622px] items-center justify-center ml-[344.167px] mt-[121.568px] relative w-[35.361px]">
                                        <div className="flex-none rotate-[351.503deg]">
                                            <div
                                                className="font-outfit font-medium not-italic text-[33.333px] tracking-[-0.7333px] text-black">
                                                <p className="leading-[20px] whitespace-pre">🥳</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className="[grid-area:1_/_1] flex h-[24.556px] items-center justify-center ml-[87.501px] mt-[128.236px] relative w-[35.328px]">
                                        <div className="flex-none rotate-[351.631deg]">
                                            <div
                                                className="font-outfit font-medium not-italic text-[33.333px] tracking-[-0.7333px] text-black">
                                                <p className="leading-[20px] whitespace-pre">🏛️</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Chat-Bubbles */}
                                    <motion.div
                                        className="[grid-area:1_/_1] flex h-[39.167px] items-center justify-center ml-0 mt-0 relative w-[201.667px]"
                                        initial={{opacity: 0, x: -30}}
                                        animate={{opacity: 1, x: 0}}
                                        transition={{duration: 0.2, delay: 0.7, ease: "easeOut"}}
                                    >
                                        <div className="flex-none h-[39.167px] w-[201.667px] rotate-[180deg]">
                                            <div
                                                className="relative size-full rounded-bl-[16.667px] rounded-br-[16.667px] rounded-tl-[8.333px] rounded-tr-[16.667px] bg-[#b282e5]">
                                                <div
                                                    aria-hidden
                                                    className="pointer-events-none absolute inset-0 rounded-bl-[16.667px] rounded-br-[16.667px] rounded-tl-[8.333px] rounded-tr-[16.667px] border-[1.083px] border-[rgba(116,65,255,0.24)] border-solid"
                                                />
                                            </div>
                                        </div>
                                    </motion.div>
                                    <motion.div
                                        className="[grid-area:1_/_1] font-outfit font-medium leading-[0] ml-[18.334px] mt-2.5 not-italic relative text-[14.167px] text-white"
                                        initial={{opacity: 0, x: -30}}
                                        animate={{opacity: 1, x: 0}}
                                        transition={{duration: 0.2, delay: 0.75, ease: "easeOut"}}
                                    >
                                        <p className="leading-[normal] whitespace-pre">We need a beach nearby!</p>
                                    </motion.div>

                                    <motion.div
                                        className="[grid-area:1_/_1] flex h-[39.167px] items-center justify-center ml-[335.834px] mt-[37.5px] relative w-[95.833px]"
                                        initial={{opacity: 0, x: 30}}
                                        animate={{opacity: 1, x: 0}}
                                        transition={{duration: 0.2, delay: 0.8, ease: "easeOut"}}
                                    >
                                        <div className="flex-none h-[39.167px] w-[95.833px] scale-y-[-100%]">
                                            <div
                                                className="relative size-full rounded-bl-[16.667px] rounded-br-[16.667px] rounded-tl-[8.333px] rounded-tr-[16.667px] bg-[#7ca8ff]">
                                                <div
                                                    aria-hidden
                                                    className="pointer-events-none absolute inset-0 rounded-bl-[16.667px] rounded-br-[16.667px] rounded-tl-[8.333px] rounded-tr-[16.667px] border-[1.083px] border-[#5f95ff] border-solid"
                                                />
                                            </div>
                                        </div>
                                    </motion.div>
                                    <motion.div
                                        className="[grid-area:1_/_1] font-outfit font-medium leading-[0] ml-[354.167px] mt-[48.334px] not-italic relative text-[14.167px] text-white"
                                        initial={{opacity: 0, x: 30}}
                                        animate={{opacity: 1, x: 0}}
                                        transition={{duration: 0.2, delay: 0.85, ease: "easeOut"}}
                                    >
                                        <p className="leading-[normal] whitespace-pre">{`& music!`}</p>
                                    </motion.div>

                                    {/* Typing */}
                                    <motion.div
                                        className="[grid-area:1_/_1] bg-[#1e3f80] h-[39.167px] ml-[128.334px] mt-[148.334px] relative w-[96.667px] rounded-bl-[16.667px] rounded-br-[16.667px] rounded-tl-[8.333px] rounded-tr-[16.667px]"
                                        initial={{opacity: 0, y: 20}}
                                        animate={{opacity: 1, y: 0}}
                                        transition={{duration: 0.2, delay: 0.9, ease: "easeOut"}}
                                    >
                                        <div
                                            aria-hidden
                                            className="pointer-events-none absolute inset-0 rounded-bl-[16.667px] rounded-br-[16.667px] rounded-tl-[8.333px] rounded-tr-[16.667px] border-[1.083px] border-[rgba(255,255,255,0.24)] border-solid"
                                        />
                                    </motion.div>
                                    <motion.div
                                        className="[grid-area:1_/_1] font-outfit font-medium leading-[0] ml-[167.501px] mt-[150px] not-italic relative text-[20px] text-white"
                                        initial={{opacity: 0, y: 20}}
                                        animate={{opacity: 1, y: 0}}
                                        transition={{duration: 0.2, delay: 0.95, ease: "easeOut"}}
                                    >
                                        <p className="leading-[normal] whitespace-pre">...</p>
                                    </motion.div>
                                </motion.div>
                            </div>

                            {/* Optionen A/B */}
                            <div
                                className="content-stretch flex items-center justify-start gap-[35px] relative w-[900px]">
                                {/* Option A – Share link */}
                                <motion.div
                                    className="content-stretch z-40 flex w-[369.167px] flex-col items-start justify-start gap-[15px] leading-[0] relative"
                                    initial={{opacity: 0, y: 30}}
                                    animate={{opacity: 1, y: 0}}
                                    transition={{duration: 0.25, delay: 0.8, ease: "easeOut"}}
                                >
                                    <motion.p
                                        className="font-outfit font-medium text-[rgba(133,140,167,0.27)] text-[16.667px] tracking-[-0.3667px]"
                                        initial={{opacity: 0, y: -10}}
                                        animate={{opacity: 1, y: 0}}
                                        transition={{duration: 0.15, delay: 0.85, ease: "easeOut"}}
                                    >
                                        Option A
                                    </motion.p>

                                    <div
                                        className="inline-grid grid-cols-[max-content] grid-rows-[max-content] place-items-start relative">
                                        <div
                                            className="[grid-area:1_/_1] pointer-events-none relative h-[223.333px] w-[369.167px] rounded-[19.224px] bg-[rgba(255,255,255,0.5)]">
                                            <div
                                                aria-hidden
                                                className="absolute inset-0 rounded-[19.224px] border-[0.915px] border-[rgba(255,255,255,0.5)] border-solid shadow-[0px_0px_13.333px_0px_rgba(0,0,0,0.07)]"
                                            />
                                            <div
                                                className="absolute inset-0 shadow-[0px_0px_5.492px_0px_inset_rgba(255,255,255,0.8)]"/>
                                        </div>

                                        <div
                                            className="[grid-area:1_/_1] font-outfit font-semibold leading-[0] ml-[33.334px] mt-[37.5px] not-italic relative text-[#232323] text-[18.333px] tracking-[-0.4033px]">
                                            <p className="leading-[20px] whitespace-pre">Share link</p>
                                        </div>

                                        <motion.div
                                            className="absolute left-[107.5px] top-[83.333px] flex items-center justify-center gap-2 h-[49.167px] w-[154.167px] rounded-[15px] cursor-pointer select-none"
                                            onClick={handleCopyLink}
                                            whileHover={{
                                                backgroundColor: "rgba(124,168,255,0.15)",
                                                transition: {duration: 0.2}
                                            }}
                                            whileTap={{scale: 0.98, transition: {duration: 0.1}}}
                                            style={{backgroundColor: "rgba(124,168,255,0.1)"}}
                                            aria-label="Copy session link"
                                        >
                                            <HyperlinkIcon/>
                                            <span
                                                className="font-outfit font-medium text-[#7ca8ff] text-[15.833px] underline decoration-solid">
    {linkCopied ? "Copied!" : "Copy Link"}
  </span>
                                        </motion.div>


                                        <div
                                            className="[grid-area:1_/_1] font-outfit font-medium leading-[0] ml-[33.334px] mt-[165.833px] not-italic relative text-[#bdc1d2] text-[13.333px] tracking-[-0.2933px]">
                                            <p className="leading-[20px] whitespace-pre">Send this link to your friends
                                                to plan together.</p>
                                        </div>

                                    </div>
                                </motion.div>

                                {/* Option B – Invite via Email */}
                                <motion.div
                                    className="content-stretch flex w-[495.833px] flex-col items-start justify-start gap-[15px] leading-[0] relative"
                                    initial={{opacity: 0, y: 30}}
                                    animate={{opacity: 1, y: 0}}
                                    transition={{duration: 0.25, delay: 0.9, ease: "easeOut"}}
                                >
                                    <motion.p
                                        className="font-outfit font-medium text-[rgba(133,140,167,0.27)] text-[16.667px] tracking-[-0.3667px]"
                                        initial={{opacity: 0, y: -10}}
                                        animate={{opacity: 1, y: 0}}
                                        transition={{duration: 0.15, delay: 0.95, ease: "easeOut"}}
                                    >
                                        Option B
                                    </motion.p>

                                    <div
                                        className="inline-grid grid-cols-[max-content] grid-rows-[max-content] place-items-start relative">
                                        {/* Card BG */}
                                        <div
                                            className="[grid-area:1_/_1] pointer-events-none relative h-[223.333px] w-[495.833px] rounded-[19.224px] bg-[rgba(255,255,255,0.5)]">
                                            <div
                                                aria-hidden
                                                className="absolute inset-0 rounded-[19.224px] border-[0.915px] border-[rgba(255,255,255,0.5)] border-solid shadow-[0px_0px_13.333px_0px_rgba(0,0,0,0.07)]"
                                            />
                                            <div
                                                className="absolute inset-0 shadow-[0px_0px_5.492px_0px_inset_rgba(255,255,255,0.8)]"/>
                                        </div>

                                        {/* Titel */}
                                        <div
                                            className="[grid-area:1_/_1] font-outfit font-semibold leading-[0] ml-[33.333px] mt-[37.5px] not-italic relative text-[#232323] text-[18.333px] tracking-[-0.4033px]">
                                            <p className="leading-[20px] whitespace-pre">Invite via Email</p>
                                        </div>

                                        {/* Add Button */}
                                        <motion.div
                                            className={`[grid-area:1_/_1] h-[49.167px] w-[92.5px] ml-[354.167px] mt-[84.167px] rounded-[15px] z-20 cursor-pointer outline-none focus:outline-none ${
                                                canAddEmail ? "bg-[#b282e5]" : "bg-[rgba(178,130,229,0.7)]"
                                            }`}
                                            onClick={canAddEmail ? handleAddEmail : undefined}
                                            whileHover={canAddEmail ? {
                                                backgroundColor: "#9a6bc7",
                                                transition: {duration: 0.2}
                                            } : {}}
                                            whileTap={canAddEmail ? {scale: 0.98, transition: {duration: 0.1}} : {}}
                                            aria-label="Add email"
                                        />

                                        {showInvitesLoading && <div className="[grid-area:1_/_1] ml-[33px] mt-[145px] w-[260px]"><GroupSkeleton variant="field" label="Einladungen werden geladen"/></div>}
                                        {/* Email-Tags (max. 2 sichtbar) */}
                                        {invitedEmails.slice(0, 2).map((emailItem, index) => {
                                            const textWidth = emailItem.length * 6.5;
                                            const tagWidth = Math.max(textWidth + 40, 85);
                                            const firstWidth = Math.max(invitedEmails[0].length * 6.5 + 40, 85);
                                            const left = index === 0 ? 33.333 : 33.333 + firstWidth + 12;

                                            return (
                                                <div
                                                    key={emailItem}
                                                    className="[grid-area:1_/_1] mt-[142.5px] flex h-[31.053px] items-center justify-between rounded-[7.763px] bg-[rgba(178,130,229,0.13)] px-[12px] relative"
                                                    style={{marginLeft: `${left}px`, width: `${tagWidth}px`}}
                                                >
                                                    <div
                                                        className="font-outfit font-medium text-[#b282e5] text-[10.868px] pr-2">
                                                        <p className="leading-[normal] whitespace-pre">{emailItem}</p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="flex-shrink-0 cursor-pointer hover:opacity-70 transition-opacity duration-150"
                                                        onClick={() => handleRemoveEmail(emailItem)}
                                                        aria-label={`Remove ${emailItem}`}
                                                    >
                                                        <SmallRemoveIcon/>
                                                    </button>
                                                </div>
                                            );
                                        })}

                                        {/* +N Indikator */}
                                        {invitedEmails.length > 2 && (() => {
                                            const firstW = Math.max(invitedEmails[0].length * 6.5 + 40, 85);
                                            const secondW = invitedEmails[1] ? Math.max(invitedEmails[1].length * 6.5 + 40, 85) : 0;
                                            const left = 33.333 + firstW + 12 + secondW + 12;
                                            return (
                                                <div
                                                    className="[grid-area:1_/_1] mt-[142.5px] flex h-[31.053px] items-center justify-center rounded-[7.763px] bg-[rgba(178,130,229,0.13)] px-[12px] relative"
                                                    style={{marginLeft: `${left}px`, width: "50px"}}
                                                >
                                                    <div
                                                        className="font-outfit font-medium text-[#b282e5] text-[10.868px]">
                                                        <p className="leading-[normal] whitespace-pre">+{invitedEmails.length - 2}</p>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Input */}
                                        <div
                                            className="[grid-area:1_/_1] bg-white h-[50px] w-[309.167px] ml-[33.334px] mt-[83.333px] relative rounded-[10px]">
                                            <div
                                                aria-hidden
                                                className="pointer-events-none absolute inset-0 rounded-[10px] border-[1.41px] border-[rgba(178,130,229,0.2)] border-solid"
                                            />
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") handleAddEmail();
                                                }}
                                                placeholder="mustermann@gmail.com"
                                                className="absolute inset-0 h-full w-full bg-transparent border-none outline-none rounded-[10px] pl-[70px] pr-[50px] py-3 font-outfit font-medium text-[14.516px] text-[#3e3e3e] placeholder:text-[#CCCCCC]"
                                                aria-label="Invite email"
                                            />
                                        </div>

                                        {/* Input-Decorations */}
                                        <div
                                            className="[grid-area:1_/_1] h-[28.203px] w-0 ml-[83.088px] mt-[94.603px] relative">
                                            <VerticalSeparatorLine/>
                                        </div>
                                        <div
                                            className="[grid-area:1_/_1] size-[15.76px] ml-[51.584px] mt-[100.41px] relative"
                                            aria-hidden>
                                            <EmailIcon/>
                                        </div>

                                        {/* Valid Tick */}
                                        {isValidEmail(email) && (
                                            <div
                                                className="[grid-area:1_/_1] size-[15.76px] ml-[304.167px] mt-[100.833px] relative">
                                                <EmailTickIcon/>
                                            </div>
                                        )}

                                        {/* Add Button Text */}
                                        <div
                                            className="[grid-area:1_/_1] z-30 pointer-events-none font-['Space_Grotesk',_sans-serif] font-bold text-[#f6f8ff] text-[15.978px] text-center tracking-[-0.8788px] ml-[400.333px] mt-[98.333px] translate-x-[-50%]">
                                            <p className="leading-[normal] whitespace-pre">Add</p>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>

                            {/* CTA + Deko-Linien */}
                            <motion.div
                                className="inline-grid grid-cols-[max-content] grid-rows-[max-content] place-items-start relative shrink-0 ml-[70px] leading-[0]"
                                initial={{opacity: 0, y: 30}}
                                animate={{opacity: 1, y: 0}}
                                transition={{
                                    opacity: {duration: 0.3, delay: 1.1, ease: "easeOut"},
                                    y: {duration: 0.3, delay: 1.1, ease: "easeOut"},
                                }}
                            >
                                {/* CTA */}
                                <motion.button
                                    type="button"
                                    className="content-stretch flex items-center justify-start gap-[13.696px] w-[198.333px] cursor-pointer rounded-[26.25px] bg-[#b282e5] pl-[29.167px] pr-[26.667px] py-[15.978px] relative ml-[180.833px] mt-[19.167px] box-border"
                                    initial={{scale: 1, boxShadow: "0 0px 0px rgba(178, 130, 229, 0)"}}
                                    animate={{scale: 1, boxShadow: "0 0px 0px rgba(178, 130, 229, 0)"}}
                                    transition={{
                                        scale: {duration: 0.0, ease: "easeOut"},
                                        boxShadow: {duration: 0.0, ease: "easeOut"},
                                    }}
                                    whileHover={{
                                        scale: 1.05,
                                        boxShadow: "0 8px 25px rgba(178,130,229,0.4)",
                                        transition: {duration: 0.2}
                                    }}
                                    whileTap={{scale: 0.98, transition: {duration: 0.1}}}
                                    onClick={SubmitFriendsRequests}
                                    aria-label="Invite Friends"
                                >
                                    <PlaneIcon/>
                                    <span
                                        className="font-['Space_Grotesk',_sans-serif] font-black text-[#f6f8ff] text-[15.978px] tracking-[-0.8788px]">
                                    Invite Friends
                                  </span>
                                </motion.button>

                                {/* Deko-Linien neben CTA */}
                                <motion.div
                                    className="[grid-area:1_/_1] h-[50px] w-[123.333px] ml-0 mt-0 relative"
                                    initial={{opacity: 0, x: -20}}
                                    animate={{opacity: 1, x: 0}}
                                    transition={{duration: 0.2, delay: 1.3, ease: "easeOut"}}
                                >
                                    <LeftFriendsLine/>
                                </motion.div>

                                <motion.div
                                    className="[grid-area:1_/_1] flex h-[50px] w-[122.5px] items-center justify-center ml-[431.667px] mt-0 relative"
                                    initial={{opacity: 0, x: 20}}
                                    animate={{opacity: 1, x: 0}}
                                    transition={{duration: 0.2, delay: 1.35, ease: "easeOut"}}
                                >
                                    <div className="flex-none">
                                        <div className="h-[50px] w-[122.5px] relative">
                                            <RightFriendsLine/>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}
